/**
 * MONSTER TOWN — ORDER GENERATION. OWNED BY THE VENUE CREW (src/venue/**).
 *
 * TWO RULES DECIDE EVERYTHING HERE:
 *
 * 1. A CUSTOMER NEVER ASKS FOR SOMETHING THE SHOP CANNOT MAKE. Availability is
 *    read off the grid — the flavours that are ORDERED are exactly the tubs the
 *    kid has actually built. Buy the mint tub and mint starts showing up in
 *    orders; that is the whole earn -> buy -> better loop made visible.
 *
 * 2. ORDERS GROW WITH WHAT YOU HAVE DONE, NEVER WITH A DIFFICULTY SETTING
 *    (LAW #3). The first customers want one scoop. After a few dozen serves a
 *    customer might want three scoops of two flavours with a cherry. Same game,
 *    same buttons, further along. There is no mode, and nothing ever gets
 *    harder than `maxScoops`.
 */

import type { SceneNode } from '../core/scene'
import type { Placement } from '../core/grid'
import type { VenueDef, VenueItem, VenueOrder } from './types'

/** Which menu items the shop can actually produce right now. */
export function availableItems(node: SceneNode, def: VenueDef): VenueItem[] {
  const built = new Set<string>()
  for (const p of node.grid.all()) built.add(p.defId)
  return def.items.filter((i) => built.has(i.stationDefId))
}

/** Everywhere a customer could sit, in placement order. */
export function seatPlacements(node: SceneNode, def: VenueDef): Placement[] {
  return node.grid.all().filter((p) => def.seatDefIds.includes(p.defId))
}

function pick<T>(list: readonly T[], rng: () => number): T | undefined {
  if (list.length === 0) return undefined
  return list[Math.min(list.length - 1, Math.floor(rng() * list.length))]
}

/**
 * Build one order. Returns null when the shop cannot make anything yet (no
 * base or no flavour built) — the customer then just sits and enjoys the room,
 * which is still not a failure.
 */
export function makeOrder(
  def: VenueDef,
  available: readonly VenueItem[],
  servedCount: number,
  rng: () => number,
): VenueOrder | null {
  const bases = available.filter((i) => i.kind === 'base')
  const scoops = available.filter((i) => i.kind === 'scoop')
  const toppings = available.filter((i) => i.kind === 'topping')
  const base = pick(bases, rng)
  if (base === undefined || scoops.length === 0) return null

  let count = 1
  if (servedCount >= 2) count = rng() < 0.5 ? 1 : 2
  if (servedCount >= 8) count = 1 + Math.floor(rng() * 3)
  count = Math.max(1, Math.min(def.maxScoops, count))

  const mixed = servedCount >= 12 && rng() < 0.4
  const first = pick(scoops, rng)
  if (first === undefined) return null
  const chosen: string[] = []
  for (let i = 0; i < count; i++) {
    const it = mixed ? (pick(scoops, rng) ?? first) : first
    chosen.push(it.id)
  }

  let topping: string | null = null
  if (servedCount >= 4 && toppings.length > 0 && rng() < 0.5) {
    topping = pick(toppings, rng)?.id ?? null
  }

  return { base: base.id, scoops: chosen, topping }
}

/** A short picture-summary of an order, for logs and the harness. Dev only. */
export function describeOrder(order: VenueOrder): string {
  const t = order.topping === null ? '' : ` + ${order.topping}`
  return `${order.base} x${order.scoops.length} [${order.scoops.join(',')}]${t}`
}
